import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_DYNAMIC_THREAT_DEFENSE } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  AntimalwareDynamicThreatDefenseDetectionLevelLevelType,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  columns,
  AntimalwareDynamicThreatDefenseProtectionLevelKey,
  convertDbValueToFormValue,
  convertFormValueToDbValue,
  rows,
} from './complex-selection-group-config';
import { GzSelectableTableRowGroupWebComponent } from '../../../../../../../ui/gzSelectableTableRowGroup';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../ui/gzButton/types';
import {
  ANTIMALWARE_DYNAMIC_THREAT_DEFENSE_DEFAULT_VALUE,
  getAntimalwareDynamicThreatDefenseActionForLocalTypeFormOptions,
  getAntimalwareDynamicThreatDefenseActionForNetworkTypeFormOptions,
  getAntimalwareDynamicThreatDefenseSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.values';
import {
  AntimalwareDynamicThreatDefenseFormValue,
  AntimalwareDynamicThreatDefenseSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.types';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { classMap } from 'lit/directives/class-map.js';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_DYNAMIC_THREAT_DEFENSE)
export class GzAntimalwareDynamicThreatDefenseWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  AntimalwareDynamicThreatDefenseFormValue,
  AntimalwareDynamicThreatDefenseSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.ANTIMALWARE,
    sectionId: SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE,
  };
  protected searchableKeywords = getAntimalwareDynamicThreatDefenseSearchableKeywords();

  @query('#antimalware-dynamic-enable-field')
  private antimalwareDynamicEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#antimalware-dynamic-info-text')
  private antimalwareDynamicInfoText: GzTextWebComponent;
  @query('#antimalware-dynamic-content')
  private antimalwareDynamicContent: HTMLElement;
  @query('#antimalware-dynamic-attack-types-field')
  private antimalwareDynamicAttackTypesField: GzFormFieldWebComponent<
    GzSelectableTableRowGroupWebComponent<
      AntimalwareDynamicThreatDefenseProtectionLevelKey,
      AntimalwareDynamicThreatDefenseDetectionLevelLevelType
    >
  >;
  @query('#antimalware-dynamic-action-for-local-field')
  private antimalwareDynamicActionForLocalField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#antimalware-dynamic-action-for-local-enable-field')
  private antimalwareDynamicActionForLocalEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-dynamic-action-for-network-field')
  private antimalwareDynamicActionForNetworkField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#antimalware-dynamic-action-for-network-enable-field')
  private antimalwareDynamicActionForNetworkEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="antimalware-dynamic-enable-field" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.HyperDetect]
          .label}"
        gz-form-input
        @toggleChange="${this.toggleAntimalwareDynamicDisabledState}"
      >
        <span
          >${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.HyperDetect]
            .translatedLabel}</span
        >
      </gz-toggle>
    </gz-form-field> `;
  }

  /**
   * Render reset button next to support link.
   */
  protected renderContentNextToSupportLink() {
    return html`<gz-button
      id="antimalware-dynamic-reset-button"
      class="${classMap({ disabled: !!this.masks.length })}"
      type="${ButtonTypes.QUIET}"
      buttonHeight="${ButtonHeights.SMALL}"
      buttonFontSize="${ButtonFontSizes.SMALL}"
      @click="${this.onResetButtonClick.bind(this)}"
      >${translateFn('Reset to default')}
    </gz-button> `;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      id="antimalware-dynamic-info-text"
      fontSize="${FontSizes.PARAGRAPH_12}"
      fontWeight="${FontWeights.NORMAL}"
      class="infoText"
      >${translateFn(
        "Hyper Detect identifies advanced attacks and suspicious activities in the pre-execution stage. It can be customized to suit your organization's security requirements."
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="antimalware-dynamic-content">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="form-subtitle"
          searchable-label="${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.ProtectionLevel]
            .label}"
          >${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.ProtectionLevel]
            .translatedLabel}</gz-title
        >

        <gz-form-field
          class="label-mw-250 mt-2"
          id="antimalware-dynamic-attack-types-field"
          label=""
          searchable-label="${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.TargetedAttack]
            .label +
          ' ' +
          this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.SuspiciousFilesAndNetworkTraffic]
            .label +
          ' ' +
          this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.Exploits].label +
          ' ' +
          this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.Ransomware].label +
          ' ' +
          this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.Grayware].label}"
        >
          <gz-selectable-table-row-group
            gz-form-input
            allowFullRowSelection
            allowFullColumnSelection
            .defaultColumn=${AntimalwareDynamicThreatDefenseDetectionLevelLevelType.NORMAL}
            .rows=${rows(this.searchableKeywords)}
            .columns=${columns()}
          ></gz-selectable-table-row-group>
        </gz-form-field>

        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="form-subtitle"
          searchable-label="${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.Actions].label}"
          >${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.Actions]
            .translatedLabel}</gz-title
        >

        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText mt-1"
          >${translateFn(
            'Actions in the drop-down menu apply to detections up to and including the selected protection level. Optionally, detections at higher levels can be reported.'
          )}
        </gz-text>
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          class="infoText"
          .innerHTML=${translateFn(
            'For example, when one attack type is set to {bold}Normal{/bold} and selected actions for {bold}Files{/bold} are {bold}Delete{/bold} and {bold}Extend reporting on higher levels{/bold}, Hyper Detect deletes the files detected at {bold}Permissive{/bold} and {bold}Normal{/bold} levels, and reports the files detected at {bold}Aggressive{/bold} level.',
            {
              bold: '<strong>',
              '/bold': '</strong>',
            }
          )}
        >
        </gz-text>

        <div class="dropdown-with-checkbox mt-2">
          <gz-form-field
            id="antimalware-dynamic-action-for-local-field"
            searchable-label="${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.Files].label}"
            label="${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.Files].translatedLabel}"
          >
            <gz-dropdown
              gz-form-input
              .options=${getAntimalwareDynamicThreatDefenseActionForLocalTypeFormOptions()}
            ></gz-dropdown>
          </gz-form-field>

          <gz-form-field id="antimalware-dynamic-action-for-local-enable-field">
            <gz-checkbox
              gz-form-input
              searchable-label="${this.searchableKeywords[
                AntimalwareDynamicThreatDefenseSearchableKeywords.ExtendReportingOnHigherLevels
              ].label}"
            >
              ${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.ExtendReportingOnHigherLevels]
                .translatedLabel}</gz-checkbox
            >
          </gz-form-field>
        </div>

        <div class="dropdown-with-checkbox">
          <gz-form-field
            id="antimalware-dynamic-action-for-network-field"
            searchable-label="${this.searchableKeywords[
              AntimalwareDynamicThreatDefenseSearchableKeywords.NetworkTraffic
            ].label}"
            label="${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.NetworkTraffic]
              .translatedLabel}"
          >
            <gz-dropdown
              gz-form-input
              .options=${getAntimalwareDynamicThreatDefenseActionForNetworkTypeFormOptions()}
            ></gz-dropdown>
          </gz-form-field>

          <gz-form-field id="antimalware-dynamic-action-for-network-enable-field">
            <gz-checkbox
              gz-form-input
              searchable-label="${this.searchableKeywords[
                AntimalwareDynamicThreatDefenseSearchableKeywords.ExtendReportingOnHigherLevels
              ].label}"
            >
              ${this.searchableKeywords[AntimalwareDynamicThreatDefenseSearchableKeywords.ExtendReportingOnHigherLevels]
                .translatedLabel}</gz-checkbox
            >
          </gz-form-field>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.antimalware.dynamicThreatDefense
   */
  protected getValueFromManager(): AntimalwareDynamicThreatDefenseFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE]: ANTIMALWARE_DYNAMIC_THREAT_DEFENSE_DEFAULT_VALUE,
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.antimalware.dynamicThreatDefense'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        antimalware: {
          dynamicThreatDefense: {
            enable: this.antimalwareDynamicEnableField,
            enableGlobalReportingForFiles: this.antimalwareDynamicActionForLocalEnableField,
            enableGlobalReportingForNetwork: this.antimalwareDynamicActionForNetworkEnableField,
            actionforLocal: this.antimalwareDynamicActionForLocalField,
            actionforNetwork: this.antimalwareDynamicActionForNetworkField,
            protectionLevel: this.antimalwareDynamicAttackTypesField,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          dynamicThreatDefense: {
            enable: this.valueT.uiSettings.antimalware.dynamicThreatDefense.enable,
            enableGlobalReportingForFiles:
              this.valueT.uiSettings.antimalware.dynamicThreatDefense.enableGlobalReportingForFiles,
            enableGlobalReportingForNetwork:
              this.valueT.uiSettings.antimalware.dynamicThreatDefense.enableGlobalReportingForNetwork,
            actionforLocal: this.valueT.uiSettings.antimalware.dynamicThreatDefense.actionforLocal,
            actionforNetwork: this.valueT.uiSettings.antimalware.dynamicThreatDefense.actionforNetwork,
            protectionLevel: convertDbValueToFormValue({
              targetedAttack: this.valueT.uiSettings.antimalware.dynamicThreatDefense.targetedAttack,
              suspiciousFilesAndNetworkTraffic:
                this.valueT.uiSettings.antimalware.dynamicThreatDefense.suspiciousFilesAndNetworkTraffic,
              exploits: this.valueT.uiSettings.antimalware.dynamicThreatDefense.exploits,
              ransomware: this.valueT.uiSettings.antimalware.dynamicThreatDefense.ransomware,
              grayware: this.valueT.uiSettings.antimalware.dynamicThreatDefense.grayware,
            }),
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // set attack types
    let attackTypes;
    attackTypes = _.get(value, ['uiSettings', 'antimalware', 'dynamicThreatDefense', 'protectionLevel'], {});
    attackTypes = convertFormValueToDbValue(attackTypes);
    _.set(value, ['uiSettings', 'antimalware', 'dynamicThreatDefense'], {
      ..._.get(value, ['uiSettings', 'antimalware', 'dynamicThreatDefense'], {}),
      ...attackTypes,
    });
    _.unset(value, ['uiSettings', 'antimalware', 'dynamicThreatDefense', 'protectionLevel']);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'antimalware', 'dynamicThreatDefense'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module depending on the read-only state and the value of the enable field.
   */
  private toggleAntimalwareDynamicDisabledState() {
    // field not rendered yet
    if (!this.antimalwareDynamicEnableField) {
      return;
    }

    toggleDisabled(this.antimalwareDynamicContent, !this.antimalwareDynamicEnableField.value);
    toggleDisabled(this.antimalwareDynamicInfoText, !this.antimalwareDynamicEnableField.value);
  }

  /**
   * Reset module to default value.
   */
  onResetButtonClick() {
    const value = _.cloneDeep(ANTIMALWARE_DYNAMIC_THREAT_DEFENSE_DEFAULT_VALUE);

    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          dynamicThreatDefense: {
            enable: value.enable,
            enableGlobalReportingForFiles: value.enableGlobalReportingForFiles,
            enableGlobalReportingForNetwork: value.enableGlobalReportingForNetwork,
            actionforLocal: value.actionforLocal,
            actionforNetwork: value.actionforNetwork,
            protectionLevel: convertDbValueToFormValue({
              targetedAttack: value.targetedAttack,
              suspiciousFilesAndNetworkTraffic: value.suspiciousFilesAndNetworkTraffic,
              exploits: value.exploits,
              ransomware: value.ransomware,
              grayware: value.grayware,
            }),
          },
        },
      },
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_DYNAMIC_THREAT_DEFENSE: GzAntimalwareDynamicThreatDefenseWebComponent;
  }
}
